import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { IcPageHeader, IcButton, IcEmptyState, IcStatusTag } from "@ukic/react";

import { api, type Device, type LatestUpdate } from "../api";
import { humanBytes, timeAgo, cpuStatus, tempStatus, diskStatus } from "../format";
import { DataTable, type Column } from "../components/DataTable";
import { DeviceStatusTag, ModeTag } from "../components/Tags";
import { Loading } from "../components/Loading";
import { ErrorState } from "../components/ErrorState";

export default function Fleet() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [latest, setLatest] = useState<LatestUpdate | null>(null);

  const load = useCallback(() => {
    setError(null);
    api
      .listDevices()
      .then(setDevices)
      .catch((e) => setError(e));
  }, []);

  useEffect(() => {
    load();
    api.getLatestUpdate().then(setLatest).catch(() => setLatest(null));
    // Auto-refresh the fleet view every 30s (heartbeat cadence).
    const t = window.setInterval(load, 30_000);
    return () => window.clearInterval(t);
  }, [load]);

  const latestVersion = latest?.version ?? null;
  const columns: Column<Device>[] = useMemo(
    () => [
      { key: "name", header: "Name", cell: (d) => <strong>{d.name}</strong> },
      { key: "location", header: "Location", cell: (d) => d.location || <span className="hl-muted">—</span> },
      {
        key: "status",
        header: "Status",
        cell: (d) => (
          <>
            <DeviceStatusTag device={d} />
            {d.recovery_active && (
              <IcStatusTag className="hl-inline-tag" label="recovery" status="danger" size="small" />
            )}
          </>
        ),
      },
      { key: "mode", header: "Mode", cell: (d) => <ModeTag mode={d.mode} /> },
      { key: "sdr", header: "SDR", cell: (d) => d.sdr_type ?? "—" },
      { key: "last_seen", header: "Last seen", cell: (d) => timeAgo(d.last_seen) },
      {
        key: "cpu",
        header: "CPU",
        align: "right",
        cell: (d) => (
          <span className={`hl-health hl-health--${cpuStatus(d.cpu_percent)}`}>
            {d.cpu_percent != null ? `${d.cpu_percent}%` : "—"}
          </span>
        ),
      },
      {
        key: "temp",
        header: "Temp",
        align: "right",
        cell: (d) => (
          <span className={`hl-health hl-health--${tempStatus(d.temp_c)}`}>
            {d.temp_c != null ? `${d.temp_c}°C` : "—"}
          </span>
        ),
      },
      {
        key: "disk",
        header: "Disk free",
        align: "right",
        cell: (d) => (
          <span className={`hl-health hl-health--${diskStatus(d.disk_free)}`}>
            {humanBytes(d.disk_free)}
          </span>
        ),
      },
      {
        key: "versions",
        header: "Versions",
        cell: (d) => {
          const outOfDate =
            !!latestVersion && !!d.agent_version && d.agent_version !== latestVersion;
          return (
            <span className="hl-muted">
              agent {d.agent_version ?? "?"} · owrx {d.owrx_version ?? "?"}
              {outOfDate && (
                <IcStatusTag
                  className="hl-inline-tag"
                  label={`update → ${latestVersion}`}
                  status="warning"
                  size="small"
                />
              )}
            </span>
          );
        },
      },
    ],
    [latestVersion],
  );

  // Only show 'active'/'disabled' here; pending devices live on the Pending page.
  const visible = (devices ?? []).filter((d) => d.status !== "pending");

  return (
    <div className="hl-page hl-stack">
      <IcPageHeader heading="Fleet" subheading="Remote SDR devices">
        <IcButton slot="actions" variant="secondary" onClick={load}>
          Refresh
        </IcButton>
      </IcPageHeader>

      {error ? (
        <ErrorState error={error} onRetry={load} />
      ) : devices === null ? (
        <Loading label="Loading fleet…" />
      ) : visible.length === 0 ? (
        <IcEmptyState
          heading="No devices yet"
          subheading="Flash a card, set WiFi and power on a device — it self-enrolls and appears here."
        />
      ) : (
        <DataTable
          caption="Fleet devices"
          columns={columns}
          rows={visible}
          rowKey={(d) => d.id}
          onRowClick={(d) => navigate(`/devices/${encodeURIComponent(d.id)}`)}
        />
      )}
    </div>
  );
}
